import React, { useMemo, useState } from 'react'
import { Card, PageTitle, StatusBadge } from '../components/Ui'
import AdminFormModal, { FormField, FormGrid } from '../components/AdminFormModal'
import { useLocalCollection } from '../hooks/useLocalCollection'
import { directors } from '../data/mockData'
import { portfolios } from '../data/adminExtendedData'
import { featuredDirectors, featuredPortfolios } from '../data/parityData'

const HERO_KEY = 'difan-admin-home-hero-v1'
const PROMO_KEY = 'difan-admin-home-promo-v1'
const DIRECTOR_KEY = 'difan-admin-home-featured-directors-v1'
const SPACE_KEY = 'difan-admin-home-featured-spaces-v1'

const heroSeed = [
  {
    id: 'hero-001',
    title: '당신의 팬심이 공간이 되는 순간',
    image: 'https://difan-jj.vercel.app/images/difan-hero.webp',
    link: '',
    order: 1,
    duration: 5,
    status: 'ACTIVE',
    startAt: '',
    endAt: '',
  },
]

const promoSeed = [
  {
    id: 'promo-001',
    label: '이벤트',
    title: '첫 AI 견적 요청 시',
    description: '추천 디렉터를 더 빠르게 매칭해드려요',
    image: '',
    link: '/ai-request',
    order: 1,
    status: 'ACTIVE',
  },
  {
    id: 'promo-002',
    label: '프리미엄 포트폴리오',
    title: '이번 달 인기 생일 카페',
    description: '최고의 디렉터가 완성한 공간을 확인하세요',
    image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuDXPkug6gNhh8LKEHINvByCGcAH7ZgSEeBhaBl0OOEw6IrpjBr21uDFlk96nAEjZs1K0F5S_MgCtx1IqZwWlqg2AI4p5rMuMNNOmLrAnZyezU1ydYpQORHhYhJjOa0H-SEWE6iOKseQ9Uam-Xb-cBCNbgDWa38eSogI7D1ejYfprU8O1fje99-BxxSmT1YyK8QEPiCSq6GKsP8x9nfhiTG6vQIuLjGnx1r_UzD0UWuw7gPBqw1uVLcnn4YAwQsqT7_7p7ljP8FL1j0',
    link: '/portfolio',
    order: 2,
    status: 'ACTIVE',
  },
]

const featuredDirectorSeed = featuredDirectors.map((item) => ({ ...item }))
const featuredSpaceSeed = (featuredPortfolios.length ? featuredPortfolios : portfolios.slice(0, 3).map((item, index) => ({ id: `fp-${index + 1}`, portfolioId: item.id, title: item.title, order: index + 1, status: 'ACTIVE', reason: '운영 추천' })))

function scrollToSlot(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function readImage(file, onSuccess, onError) {
  if (!file) return
  if (!file.type.startsWith('image/')) {
    onError('이미지 파일만 첨부할 수 있습니다.')
    return
  }
  if (file.size > 3 * 1024 * 1024) {
    onError('Mock 저장에서는 3MB 이하 이미지만 첨부할 수 있습니다. 실제 API 연결 시 Object Storage 업로드로 교체합니다.')
    return
  }
  const reader = new FileReader()
  reader.onload = () => onSuccess(String(reader.result || ''))
  reader.onerror = () => onError('이미지를 읽지 못했습니다.')
  reader.readAsDataURL(file)
}

function MediaRow({ item, type, onEdit, onDelete }) {
  const meta = type === 'hero'
    ? `${item.duration || 5}초 노출 · 순서 ${item.order}`
    : `${item.label || '배너'} · 순서 ${item.order}`
  return (
    <div className="admin-media-row">
      <div className="admin-media-thumb">{item.image ? <img src={item.image} alt="" /> : <span>이미지 미등록</span>}</div>
      <div className="admin-media-copy">
        <strong>{item.title}</strong>
        <span>{item.link || '연결 링크 없음'}</span>
        <small>{meta}</small>
      </div>
      <div className="admin-media-actions">
        <StatusBadge status={item.status} />
        <button className="button soft" type="button" onClick={() => onEdit(item)}>수정</button>
        <button className="button danger-soft" type="button" onClick={() => onDelete(item.id)}>삭제</button>
      </div>
    </div>
  )
}

export default function HomeManagement({ navigate }) {
  const { items: heroSlides, upsert: upsertHero, remove: removeHero } = useLocalCollection(HERO_KEY, heroSeed)
  const { items: promos, upsert: upsertPromo, remove: removePromo } = useLocalCollection(PROMO_KEY, promoSeed)
  const { items: selectedDirectors, setItems: setSelectedDirectors } = useLocalCollection(DIRECTOR_KEY, featuredDirectorSeed)
  const { items: selectedSpaces, setItems: setSelectedSpaces } = useLocalCollection(SPACE_KEY, featuredSpaceSeed)
  const [editor, setEditor] = useState(null)
  const [selectionType, setSelectionType] = useState(null)
  const [selectionIds, setSelectionIds] = useState([])
  const [notice, setNotice] = useState('')

  const sortedHero = useMemo(() => [...heroSlides].sort((a, b) => Number(a.order) - Number(b.order)), [heroSlides])
  const sortedPromos = useMemo(() => [...promos].sort((a, b) => Number(a.order) - Number(b.order)), [promos])
  const sortedDirectors = useMemo(() => [...selectedDirectors].sort((a, b) => Number(a.order) - Number(b.order)), [selectedDirectors])
  const sortedSpaces = useMemo(() => [...selectedSpaces].sort((a, b) => Number(a.order) - Number(b.order)), [selectedSpaces])

  const openHero = (item = null) => {
    setNotice('')
    setEditor({
      type: 'hero',
      id: item?.id || `hero-local-${Date.now()}`,
      title: item?.title || '',
      image: item?.image || '',
      link: item?.link || '',
      order: item?.order || heroSlides.length + 1,
      duration: item?.duration || 5,
      status: item?.status || 'ACTIVE',
      startAt: item?.startAt || '',
      endAt: item?.endAt || '',
    })
  }

  const openPromo = (item = null) => {
    setNotice('')
    setEditor({
      type: 'promo',
      id: item?.id || `promo-local-${Date.now()}`,
      label: item?.label || '프로모션',
      title: item?.title || '',
      description: item?.description || '',
      image: item?.image || '',
      link: item?.link || '',
      order: item?.order || promos.length + 1,
      status: item?.status || 'ACTIVE',
    })
  }

  const saveEditor = (event) => {
    event.preventDefault()
    if (!editor?.title?.trim()) return setNotice('제목은 필수입니다.')
    if (editor.type === 'hero' && !editor.image) return setNotice('히어로 슬라이드는 이미지가 필수입니다.')
    if (editor.type === 'hero') upsertHero({ ...editor, title: editor.title.trim(), order: Number(editor.order) || 1, duration: Math.max(2, Number(editor.duration) || 5) })
    else upsertPromo({ ...editor, title: editor.title.trim(), order: Number(editor.order) || 1 })
    setEditor(null)
    setNotice('저장되었습니다. 현재는 관리자 Mock 데이터로 저장되며 API 연결 시 동일 필드를 서버 저장으로 전환합니다.')
  }

  const openSelection = (type) => {
    setSelectionType(type)
    setSelectionIds(type === 'directors' ? selectedDirectors.map((item) => item.directorId) : selectedSpaces.map((item) => item.portfolioId))
    setNotice('')
  }

  const toggleSelection = (id) => {
    setSelectionIds((current) => current.includes(id) ? current.filter((item) => item !== id) : current.length >= 3 ? current : [...current, id])
  }

  const saveSelection = (event) => {
    event.preventDefault()
    if (!selectionIds.length) return setNotice('최소 1개는 선택해주세요.')
    if (selectionType === 'directors') {
      const next = selectionIds.map((id, index) => {
        const director = directors.find((item) => item.id === id)
        return { id: `fd-${id}`, directorId: id, name: director?.brandName || id, order: index + 1, status: 'ACTIVE', reason: '관리자 직접 지정', startAt: '', endAt: '' }
      })
      setSelectedDirectors(next)
    } else {
      const next = selectionIds.map((id, index) => {
        const portfolio = portfolios.find((item) => item.id === id)
        return { id: `fs-${id}`, portfolioId: id, title: portfolio?.title || id, order: index + 1, status: 'ACTIVE', reason: '관리자 직접 지정' }
      })
      setSelectedSpaces(next)
    }
    setSelectionType(null)
    setNotice('메인 노출 대상이 저장되었습니다.')
  }

  return (
    <>
      <PageTitle eyebrow="HOME CONTENT" title="메인 화면 편집" description="메인에서 관리자가 직접 등록·지정하는 영역만 모아 관리합니다. 오픈 초기에는 자동 랭킹보다 관리자 직접 지정을 우선합니다." />
      <div className="policy-callout"><strong>관리자 등록 영역과 회원·디렉터 등록 콘텐츠를 분리했습니다.</strong><span>이 화면은 히어로·프로모션 배너·인기 디렉터·추천 공간처럼 메인 노출을 직접 운영하는 곳입니다. 포트폴리오와 후기는 별도 조회·사후관리 메뉴에서 확인합니다.</span></div>
      {notice && <div className="admin-notice"><strong>안내</strong><span>{notice}</span></div>}

      <div className="home-admin-overview">
        <button className="home-admin-slot" onClick={() => scrollToSlot('home-hero-admin')}><span>01</span><strong>히어로 슬라이드</strong><small>이미지 여러 장 · 자동 전환</small></button>
        <button className="home-admin-slot" onClick={() => scrollToSlot('home-director-admin')}><span>02</span><strong>실시간 인기 디렉터</strong><small>초기 운영은 관리자 직접 지정</small></button>
        <button className="home-admin-slot" onClick={() => scrollToSlot('home-promo-admin')}><span>03</span><strong>중간 2열 배너</strong><small>이미지 · 문구 · 선택 링크</small></button>
        <button className="home-admin-slot" onClick={() => scrollToSlot('home-space-admin')}><span>04</span><strong>내 최애와 함께 특별한 공간</strong><small>추천 포트폴리오 직접 지정</small></button>
        <button className="home-admin-slot" onClick={() => navigate('/content/stories')}><span>05</span><strong>디팬 이야기·이벤트</strong><small>관리자 작성 콘텐츠</small></button>
      </div>

      <Card id="home-hero-admin" title="히어로 슬라이드" description="메인 최상단 배경 이미지를 여러 장 등록하고 각 이미지의 자동 전환 시간을 설정합니다." action={<button className="button primary" onClick={() => openHero()}>히어로 이미지 추가</button>}>
        <div id="home-hero-admin" className="admin-section-callout"><div><strong>권장 이미지 1920 × 720px</strong><span>최소 1440 × 560px 이상, 가로형 JPG/WEBP 권장 · 중요 피사체는 중앙 안전영역에 배치</span></div><span className="admin-owner-label">관리자 등록</span></div>
        <div className="admin-media-list">{sortedHero.map((item) => <MediaRow key={item.id} item={item} type="hero" onEdit={openHero} onDelete={removeHero} />)}</div>
      </Card>

      <div style={{ height: 18 }} />
      <Card title="실시간 인기 디렉터" description="자동 인기 산정 대신 오픈 초기에는 최대 3명을 운영자가 직접 지정합니다." action={<button className="button primary" onClick={() => openSelection('directors')}>디렉터 지정</button>}>
        <div id="home-director-admin" className="admin-section-callout"><div><strong>메인 노출 최대 3명</strong><span>디렉터 원본 정보는 수정하지 않고 노출 대상과 순서만 저장합니다.</span></div><span className="admin-owner-label">관리자 지정</span></div>
        <div className="admin-linked-list">{sortedDirectors.map((item) => <button type="button" key={item.id} onClick={() => navigate(`/directors/${item.directorId}`)}><div><strong>{item.order}. {item.name}</strong><span>{item.reason || '관리자 직접 지정'}</span></div><StatusBadge status={item.status} /></button>)}</div>
      </Card>

      <div style={{ height: 18 }} />
      <Card title="중간 2열 프로모션 배너" description="메인 지역별 디렉터 영역 아래의 2열 배너입니다. 이미지와 연결 링크를 선택적으로 등록할 수 있습니다." action={<button className="button primary" onClick={() => openPromo()}>배너 추가</button>}>
        <div id="home-promo-admin" className="admin-section-callout"><div><strong>권장 이미지 1200 × 260px</strong><span>PC 2열 배치를 기준으로 고해상도 가로 이미지를 권장합니다. 연결 링크는 필수가 아닙니다.</span></div><span className="admin-owner-label">관리자 등록</span></div>
        <div className="admin-media-list">{sortedPromos.map((item) => <MediaRow key={item.id} item={item} type="promo" onEdit={openPromo} onDelete={removePromo} />)}</div>
      </Card>

      <div style={{ height: 18 }} />
      <Card title="내 최애와 함께 특별한 공간" description="디렉터가 등록한 포트폴리오 중 메인에 보여줄 작업을 최대 3개 직접 지정합니다." action={<button className="button primary" onClick={() => openSelection('spaces')}>추천 공간 지정</button>}>
        <div id="home-space-admin" className="admin-section-callout"><div><strong>포트폴리오 원본은 디렉터 콘텐츠</strong><span>관리자는 포트폴리오를 새로 작성하지 않고, 공개된 작업 중 메인 노출 대상만 선택합니다.</span></div><span className="admin-owner-label">관리자 지정</span></div>
        <div className="admin-linked-list">{sortedSpaces.map((item) => <button type="button" key={item.id} onClick={() => navigate(`/content/portfolios/${item.portfolioId}`)}><div><strong>{item.order}. {item.title}</strong><span>{item.reason || '관리자 직접 지정'}</span></div><StatusBadge status={item.status} /></button>)}</div>
      </Card>

      <AdminFormModal open={Boolean(editor)} title={editor?.type === 'hero' ? '히어로 슬라이드 등록·수정' : '중간 프로모션 배너 등록·수정'} description={editor?.type === 'hero' ? '여러 장을 등록하면 노출 순서대로 자동 전환됩니다.' : '배너 연결 링크는 선택값입니다.'} onClose={() => setEditor(null)} onSubmit={saveEditor} submitLabel="저장" size="large">
        {editor && <FormGrid>
          {editor.type === 'promo' && <FormField label="라벨"><input value={editor.label} onChange={(e) => setEditor({ ...editor, label: e.target.value })} placeholder="예: 이벤트" /></FormField>}
          <FormField label="제목" required wide={editor.type === 'hero'}><input value={editor.title} onChange={(e) => setEditor({ ...editor, title: e.target.value })} placeholder="메인에 표시할 제목" required /></FormField>
          {editor.type === 'promo' && <FormField label="보조 문구" wide><input value={editor.description} onChange={(e) => setEditor({ ...editor, description: e.target.value })} placeholder="배너 보조 설명" /></FormField>}
          <FormField label="이미지 첨부" required={editor.type === 'hero'} wide hint={editor.type === 'hero' ? '권장 1920 × 720px · Mock 저장은 3MB 이하' : '권장 1200 × 260px · 이미지 없이 텍스트형 배너도 가능'}>
            <div className="admin-upload-box">
              <div className="admin-upload-preview">{editor.image ? <img src={editor.image} alt="미리보기" /> : <span>미리보기</span>}</div>
              <div className="admin-upload-copy"><span className="admin-size-guide">{editor.type === 'hero' ? '1920 × 720px 권장' : '1200 × 260px 권장'}</span><input type="file" accept="image/*" onChange={(e) => readImage(e.target.files?.[0], (image) => { setEditor({ ...editor, image }); setNotice('') }, setNotice)} />{editor.image && <button type="button" className="text-button" onClick={() => setEditor({ ...editor, image: '' })}>이미지 제거</button>}</div>
            </div>
          </FormField>
          <FormField label="연결 링크" wide hint="선택값입니다. 비워두면 클릭 이동 없이 노출만 합니다."><input value={editor.link} onChange={(e) => setEditor({ ...editor, link: e.target.value })} placeholder="예: /portfolio 또는 https://..." /></FormField>
          <FormField label="노출 순서" required><input type="number" min="1" value={editor.order} onChange={(e) => setEditor({ ...editor, order: e.target.value })} required /></FormField>
          {editor.type === 'hero' && <FormField label="이미지 전환 시간(초)" required hint="2초 이상 권장"><input type="number" min="2" max="30" value={editor.duration} onChange={(e) => setEditor({ ...editor, duration: e.target.value })} required /></FormField>}
          {editor.type === 'hero' && <FormField label="노출 시작일"><input type="date" value={editor.startAt} onChange={(e) => setEditor({ ...editor, startAt: e.target.value })} /></FormField>}
          {editor.type === 'hero' && <FormField label="노출 종료일"><input type="date" value={editor.endAt} onChange={(e) => setEditor({ ...editor, endAt: e.target.value })} /></FormField>}
          <FormField label="상태"><select value={editor.status} onChange={(e) => setEditor({ ...editor, status: e.target.value })}><option value="ACTIVE">노출</option><option value="INACTIVE">미노출</option></select></FormField>
        </FormGrid>}
      </AdminFormModal>

      <AdminFormModal open={Boolean(selectionType)} title={selectionType === 'directors' ? '실시간 인기 디렉터 지정' : '추천 공간 지정'} description="최대 3개까지 선택할 수 있으며 선택한 순서대로 메인에 노출됩니다." onClose={() => setSelectionType(null)} onSubmit={saveSelection} submitLabel="선택 저장" size="large">
        {notice && <div className="admin-notice"><strong>안내</strong><span>{notice}</span></div>}
        <div className="admin-chip-list">
          {(selectionType === 'directors' ? directors : portfolios.filter((item) => item.status === 'PUBLISHED')).map((item) => {
            const id = selectionType === 'directors' ? item.id : item.id
            const label = selectionType === 'directors' ? `${item.brandName} · ${item.categories?.slice(0, 2).join(' / ') || ''}` : `${item.title} · ${item.director}`
            return <button type="button" key={id} className={`admin-chip-button${selectionIds.includes(id) ? ' is-selected' : ''}`} onClick={() => toggleSelection(id)}>{selectionIds.includes(id) ? '✓' : '+'} {label}</button>
          })}
        </div>
        <p className="admin-answer-meta" style={{ marginTop: 14 }}>선택 {selectionIds.length}/3</p>
      </AdminFormModal>
    </>
  )
}
